import { Document, Packer, Paragraph, TextRun, AlignmentType, PageOrientation, UnderlineType } from "docx";
import type { DocState, SongChoice } from "../state";
import { hasGloria } from "../state";
import type { LyricsLib } from "../types";
import { formatFrLong } from "./date";

// The "feuille assemblée" — the folded A4-landscape, 2-column leaflet handed to
// the congregation. Formatting follows the parish's 3 mai leaflet: centred bold
// title, bold+underlined section labels with an italic value, bold refrains.
//
// Layout rules:
//  - Each section is kept together (keepLines/keepNext) so a song/ordinary is
//    never split across the page fold — unless it's taller than a column.
//  - Spacing: 2 blank lines between full sections, 1 around single-line items,
//    never more than 2 in a row.
//  - Hard requirement: fit ~2 A4. We estimate the rows and, if it overflows,
//    first drop the 2-blank gaps to 1, then (last resort) shrink the font.

const FONT = "Calibri";
const COLUMNS = 4; // 2 pages × 2 columns

interface Run {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}
interface Line {
  runs?: Run[]; // undefined ⇒ blank line
  center?: boolean;
}
interface Section {
  oneLine: boolean;
  lines: Line[];
}

const BLANK: Line = {};
const label = (label: string, value?: string): Line => ({
  runs: [{ text: `${label} :`, bold: true, underline: true }, ...(value ? [{ text: `  ${value}`, italic: true }] : [])],
});
const refrain = (text: string): Line => ({ runs: [{ text: "Refrain :  ", bold: true, italic: true }, { text, bold: true }] });
const plain = (text: string, opts: Partial<Run> = {}): Line => ({ runs: [{ text, ...opts }] });

function normTitle(t: string): string {
  return t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\(.*?\)/g, " ").replace(/[^a-z0-9]+/g, " ").trim();
}
function findLyrics(lib: LyricsLib, title: string): string {
  if (!title.trim()) return "";
  const nt = normTitle(title);
  if (lib.songs[nt]) return lib.songs[nt].lyrics;
  if (nt.length > 5) for (const k in lib.songs) if (k.startsWith(nt) || nt.startsWith(k)) return lib.songs[k].lyrics;
  return "";
}
function ordLyrics(lib: LyricsLib, setting: string, part: string): string {
  return lib.ordinaries?.[setting]?.[part] || "";
}

/** Lyric lines; refrain blocks (opening + any "R/") bold, verses normal, blank
 *  runs collapsed to one, no leading/trailing blank. */
function lyricLines(text: string, boldRefrain = false): Line[] {
  if (!text.trim()) return [plain("(paroles à compléter)", { italic: true })];
  const out: Line[] = [];
  let bold = boldRefrain;
  let pending = false;
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) {
      if (out.length) pending = true;
      continue;
    }
    if (pending) { out.push(BLANK); pending = false; }
    if (boldRefrain) {
      if (/^(r\s*[/.]|refrain\b)/i.test(line)) bold = true;
      else if (/^\d+\s*[.)]/.test(line)) bold = false;
    }
    out.push(plain(line, bold ? { bold: true } : {}));
  }
  return out;
}

/** Ordinary lyrics rendered tight: every non-empty line, no blank lines between
 *  them (the source data spaces Kyrie/Anamnèse/Agneau out line-by-line). */
function tightLines(text: string): Line[] {
  if (!text.trim()) return [plain("(paroles à compléter)", { italic: true })];
  return text.split("\n").map((x) => x.trim()).filter(Boolean).map((x) => plain(x));
}

function psalmLines(text: string): Line[] {
  const lines = text.split("\n").map((x) => x.trim()).filter((x) => x && !/^r\s*[/.]?$/i.test(x));
  const out: Line[] = [];
  lines.forEach((line, i) => {
    out.push(plain(line));
    if ((i + 1) % 4 === 0 && i < lines.length - 1) out.push(BLANK);
  });
  return out;
}

const multi = (lbl: string, value: string | undefined, body: Line[]): Section => ({ oneLine: false, lines: [label(lbl, value), BLANK, ...body] });
const one = (lbl: string, value?: string): Section => ({ oneLine: true, lines: [label(lbl, value)] });
const songSection = (lbl: string, c: SongChoice, lib: LyricsLib): Section => multi(lbl, c.title || "—", lyricLines(findLyrics(lib, c.title), true));
const ordSection = (lbl: string, set: string, part: string, lib: LyricsLib): Section => multi(lbl, set, tightLines(ordLyrics(lib, set, part)));

function planSections(s: DocState, lib: LyricsLib): Section[] {
  const l = s.liturgy;
  const out: Section[] = [];
  out.push(songSection("Chant d'entrée", s.entree, lib));
  out.push(ordSection("Demande de pardon", s.ordinary, "kyrie", lib));
  if (hasGloria(l.seasonKey)) out.push(ordSection("Gloire à Dieu", s.ordinary, "gloria", lib));
  if (l.first) out.push(one("Première lecture", `${l.first.intro || ""} (${l.first.ref})`.trim()));
  if (l.psalm) {
    const body: Line[] = [];
    if (s.psalmRefrain) body.push(refrain(s.psalmRefrain));
    if (l.psalm.text) body.push(...psalmLines(l.psalm.text));
    out.push(multi(`Psaume ${l.psalm.ref}`, undefined, body));
  }
  if (l.second) out.push(one("Deuxième lecture", `${l.second.intro || ""} (${l.second.ref})`.trim()));
  if (l.acclamation) out.push(multi("Acclamation à l'Évangile", undefined, l.acclamation.text.split("\n").filter((x) => x.trim()).map((x) => plain(x.trim(), { italic: true }))));
  if (l.gospel) out.push(one("Évangile", `${l.gospel.intro || ""} (${l.gospel.ref})`.trim()));
  out.push(one("Profession de foi", "récitée"));
  {
    const body: Line[] = [];
    if (s.prierePuRefrain) body.push(refrain(s.prierePuRefrain));
    for (const it of s.prierePuIntentions.split("\n").filter((x) => x.trim())) body.push(plain(it.trim()));
    out.push(multi("Prière universelle", undefined, body));
  }
  if (s.offertoire.title && s.offertoire.annotation !== "chorale seule") out.push(songSection("Chant d'offertoire", s.offertoire, lib));
  out.push(ordSection("Sanctus", s.ordinary, "sanctus", lib));
  out.push(ordSection("Anamnèse", s.ordinary, "anamnese", lib));
  out.push(ordSection("Agneau de Dieu", s.ordinary, "agneau", lib));
  out.push(songSection("Communion", s.communion, lib));
  out.push(songSection("Chant d'envoi", s.envoi, lib));
  return out;
}

const gapBefore = (prev: Section | undefined, cur: Section, gap2: boolean) => (!prev ? 1 : prev.oneLine || cur.oneLine ? 1 : gap2 ? 2 : 1);
const lineLen = (ln: Line) => (ln.runs || []).reduce((a, r) => a + r.text.length, 0);

function metrics(size: number) {
  const pt = size / 2; // chars per column line + lines per column, calibrated for A4-landscape 2-col
  return { cpl: Math.max(30, Math.round(680 / pt)), lpc: Math.round(490 / pt) };
}

/** Estimate the rendered rows (incl. wrapped long lines) for a config. */
function estimateRows(sections: Section[], gap2: boolean, cpl: number): number {
  let rows = 3; // title + subtitle + gap
  sections.forEach((sec, i) => {
    rows += gapBefore(sections[i - 1], sec, gap2);
    for (const ln of sec.lines) rows += ln.runs ? Math.max(1, Math.ceil(lineLen(ln) / cpl)) : 1;
  });
  return rows;
}

/** Tightest layout that fits ~2 A4: prefer 2-blank gaps & 10pt, then 1-blank,
 *  then progressively smaller font. */
function pickConfig(sections: Section[]): { gap2: boolean; size: number } {
  // Prefer 2-blank gaps at 10pt; then 1-blank gaps; only then nudge the font down.
  const candidates: [boolean, number][] = [[true, 20], [false, 20], [false, 19], [false, 18]];
  for (const [gap2, size] of candidates) {
    const { cpl, lpc } = metrics(size);
    if (estimateRows(sections, gap2, cpl) <= COLUMNS * lpc * 0.95) return { gap2, size };
  }
  return { gap2: false, size: 18 };
}

export function buildFeuille(s: DocState, lib: LyricsLib): Document {
  const l = s.liturgy;
  const sections = planSections(s, lib);
  const { gap2, size } = pickConfig(sections);

  const mkRun = (r: Run) => new TextRun({ text: r.text, font: FONT, size, bold: r.bold, italics: r.italic, underline: r.underline ? { type: UnderlineType.SINGLE } : undefined });
  const mkPara = (ln: Line, keepNext: boolean) =>
    new Paragraph({
      children: ln.runs ? ln.runs.map(mkRun) : [new TextRun({ text: "", font: FONT, size })],
      alignment: ln.center ? AlignmentType.CENTER : undefined,
      spacing: { after: 0 },
      keepLines: true,
      keepNext,
    });
  const blankPara = () => mkPara(BLANK, false);

  const body: Paragraph[] = [
    new Paragraph({ alignment: AlignmentType.CENTER, keepNext: true, children: [new TextRun({ text: formatFrLong(s.date), font: FONT, size: Math.round(size * 1.3), bold: true })] }),
    new Paragraph({ alignment: AlignmentType.CENTER, keepNext: true, children: [new TextRun({ text: `${l.dayName}${l.year ? ` — Année ${l.year}` : ""}`, font: FONT, size, bold: true })] }),
  ];

  sections.forEach((sec, i) => {
    const gap = gapBefore(sections[i - 1], sec, gap2);
    for (let k = 0; k < gap; k++) body.push(blankPara());
    sec.lines.forEach((ln, k) => body.push(mkPara(ln, k < sec.lines.length - 1))); // keep section together
  });

  return new Document({
    creator: "Animation de messe",
    title: `Feuille assemblée ${s.date}`,
    styles: { default: { document: { run: { font: FONT, size } } } },
    sections: [
      {
        properties: {
          page: {
            size: { width: 11906, height: 16838, orientation: PageOrientation.LANDSCAPE }, // A4 paysage (la lib échange l/h)
            margin: { top: 340, right: 340, bottom: 340, left: 340 },
          },
          column: { count: 2, space: 540, equalWidth: true }, // 2 colonnes (dépliant)
        },
        children: body,
      },
    ],
  });
}

export const FEUILLE_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export function feuilleFilename(s: DocState): string {
  return `${s.date}_feuille_assemblee.docx`;
}
/** Render the feuille assemblée to a .docx Blob (reused by download + share). */
export async function feuilleBlob(s: DocState, lib: LyricsLib): Promise<Blob> {
  const raw = await Packer.toBlob(buildFeuille(s, lib));
  return raw.type === FEUILLE_MIME ? raw : new Blob([raw], { type: FEUILLE_MIME });
}

export async function downloadFeuille(s: DocState, lib: LyricsLib): Promise<void> {
  const blob = await feuilleBlob(s, lib);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${s.date}_feuille_assemblee.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
